import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { stockRequestsApi, inventoryItemsApi } from '../../api/warehouse';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { RecordHeader } from '../../components/RecordHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { useToast } from '../../components/Toast';
import { STOCK_REQUEST_STATUS_LABELS } from './StockRequestsListPage';
import { STOCK_REQUEST_STAGES } from '../../config/workflow-stages';
import { formatDate } from '../../utils/formatDate';

export function StockRequestWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const stockRequestId = Number(id);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const { data: requestData, isError } = useQuery({ queryKey: ['stock-request', stockRequestId], queryFn: () => stockRequestsApi.get(stockRequestId) });
  const stockRequest = requestData?.data;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['stock-request', stockRequestId] });

  const { data: itemsData } = useQuery({ queryKey: ['stock-request-items', stockRequestId], queryFn: () => stockRequestsApi.listItems(stockRequestId) });
  const items = itemsData?.data ?? [];
  const { data: inventoryItemsData } = useQuery({ queryKey: ['inventory-items'], queryFn: () => inventoryItemsApi.list() });
  const inventoryItems = inventoryItemsData?.data ?? [];

  const [itemForm, setItemForm] = useState({ itemId: '', quantity: '' });
  const addItemMutation = useMutation({
    mutationFn: () => stockRequestsApi.addItem(stockRequestId, { itemId: Number(itemForm.itemId), quantity: Number(itemForm.quantity) }),
    onSuccess: () => { showSuccess('Item added.'); setItemForm({ itemId: '', quantity: '' }); queryClient.invalidateQueries({ queryKey: ['stock-request-items', stockRequestId] }); },
    onError: (err: ApiError) => showError(err.message),
  });

  const approveMutation = useMutation({
    mutationFn: () => stockRequestsApi.setStatus(stockRequestId, 'approved'),
    onSuccess: () => { showSuccess('Stock request approved.'); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });
  const issueMutation = useMutation({
    mutationFn: () => stockRequestsApi.issue(stockRequestId),
    onSuccess: () => { showSuccess('Stock issued.'); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const { data: docsData } = useQuery({ queryKey: ['stock-request-documents', stockRequestId], queryFn: () => genericDocumentApi.listFor('stock_request', stockRequestId) });

  if (isError) return <div className="page"><div className="error-state">Failed to load this stock request. Please try refreshing the page.</div></div>;
  if (!stockRequest) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;

  return (
    <div className="page">
      <RecordHeader
        title={stockRequest.request_reference}
        readOnly={false}
        entityType="stock_request"
        entityId={stockRequestId}
        fields={[
          { label: 'Purpose', value: stockRequest.purpose },
          { label: 'Required Date', value: formatDate(stockRequest.required_date) },
          { label: 'Status', value: <StatusBadge status={stockRequest.status} label={STOCK_REQUEST_STATUS_LABELS[stockRequest.status]} /> },
        ]}
      />

      <WorkspaceTabs
        tabs={[
          {
            key: 'items', label: 'Items', content: (
              <div>
                <SectionHeader title="Requested Items" />
                <ul>
                  {items.map((i) => <li key={i.id}>{i.description ?? `Item #${i.item_id}`} — {i.quantity} {i.unit ?? ''}</li>)}
                </ul>
                {stockRequest.status === 'pending' && (
                  <form className="inline-form" onSubmit={(e) => { e.preventDefault(); addItemMutation.mutate(); }}>
                    <select value={itemForm.itemId} onChange={(e) => setItemForm((s) => ({ ...s, itemId: e.target.value }))} required>
                      <option value="">Select item…</option>
                      {inventoryItems.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
                    </select>
                    <input type="number" step="0.01" placeholder="Quantity" value={itemForm.quantity} onChange={(e) => setItemForm((s) => ({ ...s, quantity: e.target.value }))} required />
                    <button type="submit" disabled={addItemMutation.isPending}>Add Item</button>
                  </form>
                )}
              </div>
            ),
          },
          {
            key: 'approval', label: 'Approval & Issue', content: (
              <div>
                <SectionHeader title="Approval & Issue" />
                {stockRequest.status === 'pending' && <button onClick={() => approveMutation.mutate()} disabled={approveMutation.isPending}>Approve</button>}
                {stockRequest.status === 'approved' && <button onClick={() => issueMutation.mutate()} disabled={issueMutation.isPending}>Issue Stock</button>}
                {['issued', 'partially_issued'].includes(stockRequest.status) && <p className="muted">Stock has been issued against this request.</p>}
              </div>
            ),
          },
          {
            key: 'documents', label: 'Documents', content: (
              <DocumentsSection
                documents={docsData?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('stock_request', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('stock_request', stockRequestId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('stock_request', documentId)}
                queryKeyToInvalidate={['stock-request-documents', stockRequestId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking', label: 'Tracking', content: (
              <div>
                <WorkflowTrackingTab entityType="stock_request" entityId={stockRequestId} actionStageMap={STOCK_REQUEST_STAGES} statusLabels={STOCK_REQUEST_STATUS_LABELS} />
                <CommentsThread entityType="stock_request" entityId={stockRequestId} section="warehouse" heading="Comments for this Request" />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
